// Generated using yarn generate command
import MuiTab, { TabProps } from '@mui/material/Tab';
import MuiTabs, { TabsProps } from '@mui/material/Tabs';

import React, { useState } from 'react';
import {
  tabs_base,
  tabs_item_base,
  tabs_item_large,
  tabs_item_medium,
  tabs_item_small,
  tabs_panel,
} from './Tabs.styles';
import { RemoveMuiProps } from '../types';
import type { IconName } from '../Icon/types';
import { Icon } from '../Icon';
import { clsx } from '../utils';

export type TTabsProps<T extends string = string> = RemoveMuiProps<TabsProps, 'value'> & {
  /**
   * A11y related prop
   * use useId hook from React to give a unique ID for the tabs
   */
  tabListId: string;
  size?: 'small' | 'medium' | 'large';
  /**
   * Menu items that should be rendered
   */
  items: {
    /**
     * hypen separated key for a11y tags
     */
    tabKey: string;
    label: React.ReactNode;
    value: T;
    /**
     * Colocating tab information with the panel content for easy access while combining Tabs and TabPanel component in the same file.
     *
     * When for example, the tabs are rendered in 1 component file and the tab content is far away in another file
     * and there is no shared state between them, we can pass panelContent as undefined and manually assemble them.
     */
    children?: React.ReactNode;
    icon?: IconName;
  }[];
  /**
   * Selected element
   */
  value?: T | undefined;
  /**
   * Tab item icon placement
   */
  iconPosition?: TabProps['iconPosition'];
  variant?: 'standard' | 'filled' | 'filledBrand';
};

export const Tabs = <T extends string = string>({
  tabListId,
  size = 'small',
  className,
  items,
  value: valueFromProps,
  iconPosition = 'start',
  variant = 'standard',
  ...rest
}: TTabsProps<T>) => {
  const [localValue, setLocalValue] = useState(valueFromProps ?? items[0].value);
  const handleChange = (_: React.SyntheticEvent<Element, Event>, newValue: T) => {
    rest.onChange?.(_, newValue);
    setLocalValue(newValue);
  };

  const value = valueFromProps ?? localValue;

  return (
    <MuiTabs
      className={clsx([className, `BlocksTabContainer-${variant}`])}
      css={tabs_base}
      value={value}
      {...rest}
      onChange={handleChange}
    >
      {items.map((item) => (
        <MuiTab
          key={item.value}
          iconPosition={iconPosition}
          css={[
            tabs_item_base,
            size === 'small' && tabs_item_small,
            size === 'medium' && tabs_item_medium,
            size === 'large' && tabs_item_large,
          ]}
          value={item.value}
          icon={item.icon ? <Icon name={item.icon} /> : undefined}
          label={item.label}
          className={clsx([`BlocksTabItemContainer-${variant}`])}
          disableFocusRipple
          id={getTabsA11yData(tabListId, item.tabKey).tabId}
          aria-controls={getTabsA11yData(tabListId, item.tabKey).tabPanelId}
        />
      ))}
    </MuiTabs>
  );
};

type TabPanelProps = {
  isSelected: boolean;
  tabListId: string;
  keepMounted?: boolean;
  /** Value is ignored, just added in types to prevent conflicts if the same Tab object is reused for Panel and Tabs */
  value?: unknown;
} & Omit<TTabsProps['items'][number], 'label' | 'icon' | 'value'> &
  React.HTMLAttributes<HTMLDivElement>;

/**
 * Refer to https://mui.com/material-ui/react-tabs/#introduction for more information
 */
export function TabPanel(props: TabPanelProps) {
  const { keepMounted, isSelected, tabKey, value, children, ...htmlDivProps } = props;

  return (
    <div
      css={tabs_panel}
      role="tabpanel"
      hidden={!isSelected}
      tabIndex={isSelected ? 0 : -1}
      id={getTabsA11yData(props.tabListId, tabKey).tabPanelId}
      aria-labelledby={getTabsA11yData(props.tabListId, tabKey).tabId}
      {...htmlDivProps}
    >
      {(keepMounted || isSelected) && children}
    </div>
  );
}

export function getTabsA11yData(tabListId: string, key: string) {
  return {
    tabId: `spt-tab-${tabListId}-${key}`,
    tabPanelId: `spt-tabpanel-${tabListId}-${key}`,
  };
}
